import { StateService } from '@uirouter/angular';
import { Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import dayjs from 'dayjs';
import pick from 'lodash-es/pick';
import extend from 'lodash-es/extend';
import isString from 'lodash-es/isString';
import mapValues from 'lodash-es/mapValues';

import { ApplicantResource } from '@app/shared/modules/resources/applicant.resource';
import { IFBActivity } from '@interfaces';
import { JobApplicationResource } from '@app/shared/modules/resources/job-application.resource';
import { JobResource } from '@app/shared/modules/resources/job.resource';
import { logger } from '@utils/logger.service';
import { UserApplicationResource } from '@app/shared/modules/resources/user-application.resource';
import { UserManager } from '@app/shared/services/user-manager.service';
import { UserResource } from '@app/shared/modules/resources/user.resource';

@Injectable({
  providedIn: 'root'
})
export class PopulateActivityService {
  constructor(
    private applicantResource: ApplicantResource,
    private jobApplicationResource: JobApplicationResource,
    private jobResource: JobResource,
    private stateService: StateService,
    private userApplicationResource: UserApplicationResource,
    private userManager: UserManager,
    private userResource: UserResource
  ) {}

  private object$(activity: IFBActivity) {
    const name = activity.object.name;

    if (name === 'job_application' && !this.userManager.isTalentScout()) {
      return this.jobApplicationResource.getById(activity.object.value, { expand: 'job,referrer,applicant' });
    } else if (name === 'job_application' && this.userManager.isTalentScout()) {
      let userId = activity.user_id;
      if (!userId && activity.actor.name === 'user') {
        userId = activity.actor.value;
      }

      return this.userApplicationResource.getById(activity.object.value, {}, userId);
    } else if (name === 'job' || activity.job_id) {
      return this.jobResource.get(activity.job_id || activity.object.value);
    } else {
      // return a rejected promise
      return of(null);
    }
  }

  // parseISO8601String : string -> Date
  // Parse an ISO-8601 date, including possible timezone,
  // into a Javascript Date object.
  //
  // Test strings: parseISO8601String(x).toISOString()
  // "2013-01-31T12:34"              -> "2013-01-31T12:34:00.000Z"
  // "2013-01-31T12:34:56"           -> "2013-01-31T12:34:56.000Z"
  // "2013-01-31T12:34:56.78"        -> "2013-01-31T12:34:56.780Z"
  // "2013-01-31T12:34:56.78+0100"   -> "2013-01-31T11:34:56.780Z"
  // "2013-01-31T12:34:56.78+0530"   -> "2013-01-31T07:04:56.780Z"
  // "2013-01-31T12:34:56.78-0330"   -> "2013-01-31T16:04:56.780Z"
  // "2013-01-31T12:34:56-0330"      -> "2013-01-31T16:04:56.000Z"
  // "2013-01-31T12:34:56Z"          -> "2013-01-31T12:34:56.000Z"
  parseISO8601String(dateString: string) {
    const timebits =
      /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2})(?::([0-9]*)(\.[0-9]*)?)?(?:([+-])([0-9]{2}[:]?)([0-9]{2}))?/;
    const m = timebits.exec(dateString);
    let resultDate;
    if (m) {
      let utcdate = Date.UTC(
        parseInt(m[1], 10),
        parseInt(m[2], 10) - 1, // months are zero-offset (!)
        parseInt(m[3], 10),
        parseInt(m[4], 10),
        parseInt(m[5], 10), // hh:mm
        (m[6] && parseInt(m[6], 10)) || 0, // optional seconds
        (m[7] && parseFloat(m[7]) * 1000) || 0 // optional fraction
      );
      // utcdate is milliseconds since the epoch
      if (m[9] && m[10]) {
        const offsetMinutes = parseInt(m[9], 10) * 60 + parseInt(m[10], 10);
        utcdate += (m[8] === '+' ? -1 : +1) * offsetMinutes * 60000;
      }
      resultDate = new Date(utcdate);
    } else {
      resultDate = null;
    }
    return resultDate;
  }

  populateUserFromResponse(activity: IFBActivity, response: any) {
    activity.user = pick(response, 'id', 'incognito', 'firstName', 'lastName', 'profile_picture');

    // You only show Call To Action for user-related activities if: 1) User is present 2) Activity is 'followed'
    if (activity.user.firstName && activity.user.lastName && activity.type === 'notification-stream.followed') {
      activity.show_cta = true;
    }

    if (activity.verb === 'applied') {
      this.makeAppliedIndirect(activity);
    }
  }

  findUserId(activity: IFBActivity) {
    let userId;
    // Actor (when it's 'user') has priority, because inApp notifications
    // have userId as target of the notification and not the Actor of the notification
    if (activity.actor && activity.actor.name === 'user') {
      userId = activity.actor.value;
    } else if (activity.user_id) {
      userId = activity.user_id;
    } else if (activity.object && activity.object.name === 'user') {
      userId = activity.object.value;
    }
    return userId;
  }

  makeAppliedIndirect(activity: IFBActivity) {
    const notCurrentUser = !activity.user || (activity.user && activity.user.id !== this.userManager.data.id);
    // Override the verb if the user in the activity is not the current user
    // Needed for notifications where the RT/CA is notified because they are in the hiring team/the contact person
    if (notCurrentUser) {
      activity.type = 'notification-stream.applied_indirectly';
    } else {
      activity.type = 'notification-stream.applied';
    }
  }

  /**
   * Understands activity object structure and populates it with async dependencies
   *
   * @param activity - getstream.io activity object
   * @param typeDetector - function to resolve activity type, returns string
   * @param observablesStack - array to write observables to to track when activity finished loading
   */
  populate(activity: IFBActivity, typeDetector: (arg0: IFBActivity) => string, observablesStack: any[]) {
    observablesStack = observablesStack || [];
    let request$: any;

    // transform string properties like: "job:xxxx-xxxx" to an object of { name: "job", value: "xxxx-xxxx" }
    const activityWithParsedObjects = mapValues(activity, (val: any) => {
      let parts;
      let result = val;

      if (isString(val)) {
        parts = val.split(':');

        if (parts.length === 2) {
          result = {
            name: parts[0],
            value: parts[1]
          };
        }
      }

      return result;
    });

    // it's required to keep a reference to original activity object instead of overriding it
    extend(activity, activityWithParsedObjects);

    activity.type = typeDetector(activity);
    const isDueRewardReminder = activity.object && activity.object.name === 'reward' && activity.verb === 'due';

    if (activity.time) {
      const activityDate: any = this.parseISO8601String(activity.time);

      // If less than one day
      if (Date.now() - activityDate < 86400000) {
        // display time elapsed
        activity.timeElapsed = dayjs(activityDate).fromNow();
        activity.timeElapsed = activity.timeElapsed.replace(/((\bminutes?\b)|(\bMinuten?\b))/gi, 'm');
        activity.timeElapsed = activity.timeElapsed.replace(/((\bhours?\b)|(\bStunden?\b))/gi, 'h');
      } else {
        // display date
        activity.timeElapsed = dayjs(activityDate).format('D MMM');
      }
    }

    // query nested subresources
    if (activity.object && activity.object.name && !activity.ready) {
      request$ = this.object$(activity).pipe(
        catchError(err => {
          logger.error(err);
          return of(null);
        }),
        tap(response => {
          if (response === null) {
            const isUserObject = activity.object && activity.object.name === 'user';

            // If promise was rejected mark as incomplete
            // Except, when object is 'user', because it will be handled somewhere else
            if (!isUserObject) {
              activity.incomplete = true;
            }

            // Needed for notifications where the referrer has been deleted
            if (activity.verb === 'applied') {
              this.makeAppliedIndirect(activity);
            }
          } else {
            if (activity.object.name === 'job' || activity.job_id) {
              activity.job = pick(response, 'id', 'title');
            }

            if (activity.object.name === 'job_application') {
              activity.job_application = pick(response, 'id');

              if (response.closeReason) {
                activity.closeReason = 'referrals.actions.close.reason.' + response.closeReason;
              }

              if (response.applicant) {
                activity.applicant = response.applicant;

                const isApplicantExpanded = response.applicant.firstName && response.applicant.lastName;
                if (isApplicantExpanded && activity.type === 'notification-stream.candidate_referred') {
                  // Applicant is expanded instead of requested, if it exists, CTA should be displayed
                  activity.show_cta = true;
                }
              }

              if (response.applicantImage) {
                activity.applicant = activity.applicant || {};
                activity.applicant.profile_picture = response.applicantImage;
              }

              if (activity.job) {
                extend(activity.job, response.job);
              } else {
                activity.job = response.job;
              }

              // If actor is user, then user data is already being loaded and
              // this would override it with the notificationTarget instead of
              // the actual actor
              if (activity.actor && activity.actor.name && activity.actor.name !== 'user') {
                if (activity.user) {
                  extend(activity.user, response.referrer);
                } else {
                  activity.user = response.referrer;
                }
              }

              if (activity.verb === 'applied') {
                this.makeAppliedIndirect(activity);
              }
            }

            if (activity.job && activity.job.id) {
              const href = this.stateService.href('job-advert', { id: activity.job.id });
              activity.job.href = href.endsWith(activity.job.id) ? href : `${href}/${activity.job.id}`;
            }
          }
        })
      );

      observablesStack.push(request$);
    }

    // Get User
    const userId = this.findUserId(activity);
    if (userId && !isDueRewardReminder) {
      this.userResource.getById(userId).subscribe(
        response => {
          this.populateUserFromResponse(activity, response);
        },
        err => {
          // if rejected mark as incomplete
          logger.error(err);
          activity.incomplete = true;
        }
      );
      observablesStack.push(of(null));
    }

    const applicantId =
      activity.actor && activity.actor.name === 'applicant' ? activity.actor.value : activity.applicant_id;
    if (applicantId) {
      request$ = this.applicantResource.get(applicantId).pipe(
        catchError(err => {
          logger.error(err);
          return of(null);
        }),
        tap(response => {
          if (activity.applicant) {
            extend(activity.applicant, pick(response, 'id', 'firstName', 'lastName'));
          } else {
            activity.applicant = pick(response, 'id', 'firstName', 'lastName');
          }

          activity.show_cta = true;
        })
      );

      observablesStack.push(request$);
    }

    const isRewardAccepted = activity.object && activity.object.name === 'reward' && activity.verb === 'accepted';
    if (isRewardAccepted && activity.referral_id) {
      request$ = this.applicantResource.get(activity.referral_id).pipe(
        tap(response => {
          if (activity.applicant) {
            extend(activity.applicant, pick(response, 'id', 'firstName', 'lastName'));
          } else {
            activity.applicant = pick(response, 'id', 'firstName', 'lastName');
          }
        }),
        catchError(err => {
          logger.error(err);
          return of(null);
        })
      );

      observablesStack.push(request$);
    }

    if (isDueRewardReminder && activity.recipient_id) {
      activity.rewardId = activity.object.value;
      request$ = this.userResource.getById(activity.recipient_id).pipe(
        tap(response => {
          activity.user = pick(response, 'id', 'firstName', 'lastName');
        }),
        catchError(err => {
          logger.error(err);
          return of(null);
        })
      );

      observablesStack.push(request$);
    }

    // Check if show_cta
    if (isDueRewardReminder || isRewardAccepted) {
      activity.show_cta = true;
    }
  }
}
